/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListOrganizationUsersRequest,
  ListOrganizationUsersSecurity,
} from "../models/operations/listorganizationusers.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildListOrganizationUsersQuery,
  ListOrganizationUsersQueryData,
  prefetchListOrganizationUsers,
  queryKeyListOrganizationUsers,
} from "./listOrganizationUsers.core.js";
export {
  buildListOrganizationUsersQuery,
  type ListOrganizationUsersQueryData,
  prefetchListOrganizationUsers,
  queryKeyListOrganizationUsers,
};

export type ListOrganizationUsersQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listUsers organizations
 *
 * @remarks
 * List users in the active organization from Speakeasy organization_user_relationships.
 */
export function useListOrganizationUsers(
  request?: ListOrganizationUsersRequest | undefined,
  security?: ListOrganizationUsersSecurity | undefined,
  options?: QueryHookOptions<
    ListOrganizationUsersQueryData,
    ListOrganizationUsersQueryError
  >,
): UseQueryResult<
  ListOrganizationUsersQueryData,
  ListOrganizationUsersQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildListOrganizationUsersQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listUsers organizations
 *
 * @remarks
 * List users in the active organization from Speakeasy organization_user_relationships.
 */
export function useListOrganizationUsersSuspense(
  request?: ListOrganizationUsersRequest | undefined,
  security?: ListOrganizationUsersSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    ListOrganizationUsersQueryData,
    ListOrganizationUsersQueryError
  >,
): UseSuspenseQueryResult<
  ListOrganizationUsersQueryData,
  ListOrganizationUsersQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildListOrganizationUsersQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setListOrganizationUsersData(
  client: QueryClient,
  queryKeyBase: [parameters: { gramSession?: string | undefined }],
  data: ListOrganizationUsersQueryData,
): ListOrganizationUsersQueryData | undefined {
  const key = queryKeyListOrganizationUsers(...queryKeyBase);

  return client.setQueryData<ListOrganizationUsersQueryData>(key, data);
}

export function invalidateListOrganizationUsers(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: { gramSession?: string | undefined }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "organizations", "listUsers", ...queryKeyBase],
  });
}

export function invalidateAllListOrganizationUsers(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "organizations", "listUsers"],
  });
}
